"use client";

import { useCallback, useState } from "react";
import axios from "axios";
import { registerApi } from "../services/auth.api";
import { isBackendUnavailableError } from "../utils/isBackendUnavailableError";

type ErrorResponse = {
  message?: string;
  errors?: {
    email?: string[];
    "g-recaptcha-response"?: string[];
  };
};

type RegisterPayload = {
  fname: string;
  lname: string;
  email: string;
  password: string;
  password_confirmation: string;
  region?: string | null;
  "g-recaptcha-response"?: string;
  /**
   * Registration asks for credentials only; gender, birthday, phone, the
   * country/timezone and the terms are collected on the second step that email
   * and Google signups share. They stay optional here rather than being
   * deleted so a caller that still sends the full profile keeps working - the
   * API validates each one when present.
   */
  gender?: string;
  birthday?: string | null;
  country?: string;
  phone?: string;
  timezone?: string;
  terms?: boolean;
  referral_code?: string | null;
};

export function useRegister() {
  const [loading, setLoading] = useState(false);
  const [errorKey, setErrorKey] = useState("");

  // Clears the banner from a previous attempt. handleRegister() resets it
  // too, but a submit can end before ever reaching the request (a reCAPTCHA
  // token that won't come), and leaving the old error on screen makes a retry
  // look like nothing happened - same fix as useLogin's.
  const resetError = useCallback(() => setErrorKey(""), []);

  const handleRegister = async (data: RegisterPayload) => {
    setLoading(true);
    setErrorKey("");

    try {
      // registerApi() already authenticates the account (the backend mints
      // a token at registration time), so there's no separate login step -
      // just flag that a verification code has already been sent for the
      // inactive-account modal (see EmailVerificationContext) to pick up.
      const res = await registerApi(data);

      if (typeof window !== "undefined") {
        sessionStorage.setItem("justRegistered", "true");
      }

      return res;
    } catch (err: unknown) {
      let key = "register_failed";

      if (isBackendUnavailableError(err)) {
        key = "backend_unavailable";
      } else if (axios.isAxiosError<ErrorResponse>(err)) {
        const emailErrors = err.response?.data?.errors?.email ?? [];
        const recaptchaErrors =
          err.response?.data?.errors?.["g-recaptcha-response"] ?? [];

        if (recaptchaErrors.length > 0) {
          key = "recaptcha_failed";
        } else if (emailErrors.length > 0) {
          key = "email_taken";
        }
      }

      setErrorKey(key);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { handleRegister, loading, errorKey, resetError };
}